import type { EventSourceMessage } from '@microsoft/fetch-event-source';
import { fetchEventSource } from '@microsoft/fetch-event-source';

import { getEnv } from '@luna/env';
import { logger } from '@luna/logger';

import { request } from '../request/request';

const ONE_SECOND = 1000;
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const ONE_MINUTE = 60 * ONE_SECOND;
const INITIAL_BACKOFF = ONE_SECOND;
const BACKOFF_MULTIPLIER = 2;
const MAX_BACKOFF = ONE_MINUTE;
const NO_PAYLOAD = '';

function getEventUrlFallback({ subscriptionId }: { subscriptionId: string }): string {
    return `${getEnv().eventApiUrl}/notification-connector/public/events/${subscriptionId}`;
}

async function createSubscription({ signal }: { signal: AbortSignal }): Promise<string> {
    let location = '';

    const data = await request.post<{ subscriptionId: string }>(
        `${getEnv().apiGatewayUrl}notification-core/public/subscription`,
        NO_PAYLOAD,
        {
            signal,
            onResponse: (response: Response) => {
                location = response.headers.get('location') ?? '';
            },
        },
    );

    return location || getEventUrlFallback({ subscriptionId: data.subscriptionId });
}

function getNextBackoff(currentBackoff: number, maxBackoff: number): number {
    return Math.min(currentBackoff * BACKOFF_MULTIPLIER, maxBackoff);
}

export function streamSseEvents<T extends EventSourceMessage = EventSourceMessage>({
    onEvent,
    onOpen,
    onClose,
    onError,
    onBackoff,
    initialBackoff = INITIAL_BACKOFF,
    maxBackoff = MAX_BACKOFF,
    signal,
}: {
    signal: AbortSignal;
    onEvent?: (event: T) => void;
    onError?: (error: Error) => void;
    onOpen?: (response: Response) => void;
    onClose?: () => void;
    onBackoff?: (backoff: number) => void;
    initialBackoff?: number;
    maxBackoff?: number;
}): Promise<void> {
    return new Promise((resolve) => {
        let backoff = initialBackoff;
        let timeout: NodeJS.Timeout | null = null;

        async function subscribe(): Promise<void> {
            const location = await createSubscription({ signal });

            if (!location) {
                throw new Error('Failed to create subscription');
            }

            return fetchEventSource(request.mutateUrl(location), {
                signal,
                openWhenHidden: true,
                onopen: async (response) => {
                    if (!response.ok) {
                        throw new Error('Failed to open SSE connection');
                    }

                    backoff = initialBackoff;
                    onOpen?.(response);
                },
                onmessage: (event) => {
                    logger.debug('[SSEEvents]: Event', event);
                    onEvent?.(event as T);
                },
                onerror: (error) => {
                    onError?.(error);
                    logger.error('[SSEEvents] Error', error);
                    throw error;
                },
                onclose: () => {
                    onClose?.();
                    logger.debug('[SSEEvents]: Connection closed');
                },
            });
        }

        function subscribeInfinitelyWithBackoff(): void {
            function retryWithBackoff(): void {
                onBackoff?.(backoff);
                timeout = setTimeout(() => {
                    subscribeInfinitelyWithBackoff();
                }, backoff);
                backoff = getNextBackoff(backoff, maxBackoff);
            }

            subscribe()
                .then(() => {
                    if (signal.aborted) {
                        return;
                    }
                    logger.debug('[SSEEvents]: Retry initiated due to connection close');
                    retryWithBackoff();
                })
                .catch((error) => {
                    if (signal.aborted) {
                        return;
                    }

                    logger.debug('[SSEEvents]: Retry initiated due to error', error);
                    retryWithBackoff();
                });
        }

        signal.addEventListener('abort', () => {
            logger.debug('[SSEEvents]: Aborted');

            if (timeout) {
                clearTimeout(timeout);
            }

            resolve();
        });

        subscribeInfinitelyWithBackoff();
    });
}
